import { supabase } from './supabase';

/**
 * Search facilities using Supabase full-text search
 * @param {string} query - Search query
 * @param {number} limit - Results limit
 * @returns {Promise<Array>} - Facility results
 */
export async function searchFacilities(query, limit = 50) {
  try {
    if (!query || query.trim().length === 0) {
      return [];
    }

    // Use direct search on the table
    const { data, error } = await supabase
      .from('facilities')
      .select('id, FacilityID, FacilityName, FacilityDescription, FacilityTypeDescription, FacilityLatitude, FacilityLongitude')
      .or(`FacilityName.ilike.%${query}%,FacilityDescription.ilike.%${query}%,FacilityTypeDescription.ilike.%${query}%,Keywords.ilike.%${query}%`)
      .limit(limit);

    if (error) {
      console.error('Search error:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error searching facilities:', error);
    return [];
  }
}

/**
 * Get all facilities with pagination
 * @param {number} limit - Results limit per page
 * @param {number} offset - Results offset
 * @returns {Promise<{data: Array, count: number}>} - Facilities and total count
 */
export async function getFacilities(limit = 50, offset = 0) {
  try {
    const { data, count, error } = await supabase
      .from('facilities')
      .select('*', { count: 'exact' })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('Fetch error:', error);
      throw error;
    }

    return {
      data: data || [],
      count: count || 0,
    };
  } catch (error) {
    console.error('Error fetching facilities:', error);
    return {
      data: [],
      count: 0,
    };
  }
}

/**
 * Get facility count in database
 * @returns {Promise<number>} - Total facility count
 */
export async function getFacilitiesCount() {
  try {
    const { count, error } = await supabase
      .from('facilities')
      .select('*', { count: 'exact', head: true });

    if (error) {
      console.error('Count error:', error);
      throw error;
    }

    return count || 0;
  } catch (error) {
    console.error('Error getting facilities count:', error);
    return 0;
  }
}

/**
 * Check if sync is still running or completed
 * This polls the database to check if facilities have been populated
 * @returns {Promise<{synced: boolean, count: number}>}
 */
export async function checkSyncStatus() {
  try {
    const count = await getFacilitiesCount();
    return {
      synced: count > 0,
      count: count,
    };
  } catch (error) {
    console.error('Error checking sync status:', error);
    return {
      synced: false,
      count: 0,
    };
  }
}
